import { PropsWithChildren, useCallback, useLayoutEffect, useRef, useState } from 'react';
import Animated from '../animated/animated';
import Page from '../page/page';
import styles from './hub.module.css';
import { useSearchParams } from 'react-router-dom';

interface HubItem {
	title: string;
}
interface Props extends PropsWithChildren {
	title: string;
	index?: number;
	hubItems: HubItem[];
}
export default function Hub({ title, hubItems, children, index }: Props) {
	const hubContainer = useRef<HTMLDivElement | null>(null);
	const [currentIndex, setCurrentIndex] = useState(index ?? 0);
	const [searchParams, setSearchParams] = useSearchParams();
	let timeoutId: NodeJS.Timeout | null = null;

	const scrollToIndex = useCallback(
		(i: number, behavior: ScrollBehavior) => {
			hubContainer.current?.scrollTo({
				left: (hubContainer.current.scrollWidth / hubItems.length) * i,
				behavior,
			});
		},
		[hubItems.length],
	);

	useLayoutEffect(() => {
		scrollToIndex(currentIndex, 'instant');
	}, [currentIndex, scrollToIndex]);

	const onScroll = () => {
		// Calculate the index correctly for web/desktop version
		if (hubContainer.current) {
			const index = (hubContainer.current.scrollLeft / (hubItems.length * screen.width)) * hubItems.length;
			const indexForSearchParams = Math.round(index);
			if (Number.isInteger(index) && index !== currentIndex) {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
				setCurrentIndex(index);
				setSearchParams({ index: index.toString() }, { replace: true });
			} else {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
				if (indexForSearchParams !== Number(searchParams.get('index'))) {
					timeoutId = setTimeout(() => {
						setCurrentIndex(indexForSearchParams);
						setSearchParams({ index: indexForSearchParams.toString() }, { replace: true });
					}, 100);
				}
			}
		}
	};

	const onTitleClick = (i: number) => {
		scrollToIndex(i, 'smooth');
	};

	return (
		<div ref={hubContainer} className={styles.parallax} onScroll={onScroll}>
			<div className={styles.title}>
				<Animated>{title}</Animated>
			</div>

			<div className={styles.container}>
				{hubItems.map((hubItem, i) => {
					return (
						<Page
							key={hubItem.title}
							title={hubItem.title}
							onTitleClick={() => {
								onTitleClick(i);
							}}>
							{children ? children[i] : undefined}
						</Page>
					);
				})}
			</div>
			<div className={styles['dots-container']}>
				{hubItems.map((hubItem, i) => {
					return (
						<div
							key={i}
							onClick={() => {
								onTitleClick(i);
							}}>
							<div className={`${styles.dots} ${i === currentIndex ? styles.active : ''}`}></div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
